import { jssPreset, StylesProvider, ThemeProvider } from '@mui/styles';
import { Theme } from '@emotion/react';

import { create as createJSS } from 'jss';
import vendorPrefixer from 'jss-vendor-prefixer';
import React, { ReactNode } from 'react';
import { JSS_LABEL, setupStylesInjectionPoint } from '../utils/jss-helpers';
import { appTheme } from './mui-themes';
import enGB from 'date-fns/locale/en-GB';
import enUS from 'date-fns/locale/en-US';
import fr from 'date-fns/locale/fr';
import frCA from 'date-fns/locale/fr-CA';
import I18nProvider from 'carbon-react/lib/components/i18n-provider';
import { useIntl } from 'react-intl';
import { SupportedLocaleCode } from '@futrli/localisation';
import { enGB as enGBCarbon, frFR, frCA as frCACarbon, enUS as enUSCarbon } from 'carbon-react/lib/locales';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export interface UIComponentsProviderProps {
  theme?: Theme;
}

export type UIComponentsTheme = UIComponentsProviderProps['theme'];

const jss = createJSS({
  plugins: [...jssPreset().plugins, vendorPrefixer()],
  insertionPoint: JSS_LABEL
});

setupStylesInjectionPoint(JSS_LABEL);

const locales: Record<SupportedLocaleCode, Locale> = {
  'en-GB': enGB,
  'fr-FR': fr,
  'fr-CA': frCA,
  'en-test1': enGB,
  'en-test2': enUS
};
const carbonLocales = {
  'en-GB': enGBCarbon,
  'fr-FR': frFR,
  'fr-CA': frCACarbon,
  'en-test1': enGBCarbon,
  'en-test2': enUSCarbon
};

export const UIComponentsProvider = ({ children, theme = appTheme }: { children: ReactNode; theme?: Theme }) => {
  const intl = useIntl();
  const baseCarbonLocale = carbonLocales[intl.locale as keyof typeof carbonLocales] || enGBCarbon;

  const carbonLocaleOverride = {
    ...baseCarbonLocale,
    date: {
      dateFnsLocale: () => locales[intl.locale as SupportedLocaleCode],
      ariaLabels: {
        previousMonthButton: () => 'prev-month-button',
        nextMonthButton: () => 'next-month-button'
      }
    },
    select: {
      actionButtonText: baseCarbonLocale.select?.actionButtonText ?? enGBCarbon.select.actionButtonText,
      noResultsForTerm: baseCarbonLocale.select?.noResultsForTerm ?? enGBCarbon.select.noResultsForTerm,
      placeholder: () =>
        intl.formatMessage({ id: 'common.predict.please_select', defaultMessage: 'Please select...' })
    }
  };

  return (
    <StylesProvider jss={jss}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={intl.locale}>
          <I18nProvider locale={carbonLocaleOverride}>
            {children}
            {/* <MuiPickersUtilsProvider utils={DateFnsUtils}></MuiPickersUtilsProvider> */}
          </I18nProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </StylesProvider>
  );
};
